const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const NAME_RE = /^[A-Za-z]+(?:[ '-][A-Za-z]+)*$/;
const PAN_RE = /^[A-Z]{5}[0-9]{4}[A-Z]$/;
const MANAGER_ID_RE = /^[A-Za-z0-9_-]+$/;

const MAX_FILE_MB = 10;
const MAX_IMAGE_MB = 5;

function isPastDate(value) {
  if (!value) return true;
  const d = new Date(value);
  return !Number.isNaN(d.getTime()) && d < new Date();
}

function isAtLeastAge(value, years = 18) {
  if (!value) return true;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return false;
  const cutoff = new Date();
  cutoff.setFullYear(cutoff.getFullYear() - years);
  return d <= cutoff;
}

function validateFile(file, { required = false, imageOnly = false } = {}) {
  if (!file) {
    return required ? "This document is required" : "";
  }
  const maxMb = imageOnly ? MAX_IMAGE_MB : MAX_FILE_MB;
  if (file.size > maxMb * 1024 * 1024) {
    return `File must be ${maxMb} MB or smaller`;
  }
  if (imageOnly && !file.type.startsWith("image/")) {
    return "Please upload an image file (JPG or PNG)";
  }
  const allowed = imageOnly
    ? ["image/jpeg", "image/png", "image/webp"]
    : ["image/jpeg", "image/png", "application/pdf"];
  if (!allowed.includes(file.type)) {
    return imageOnly ? "Use JPG or PNG format" : "Use PDF, JPG, or PNG format";
  }
  return "";
}

export function validatePersonalInfo(form) {
  const errors = {};
  const name = String(form.name || "").trim();
  const email = String(form.email || "").trim();
  const contact = String(form.contactNumber || "").trim();
  const pan = String(form.panNumber || "").trim().toUpperCase();
  const aadhar = String(form.aadharNumber || "").trim();

  if (!name) errors.name = "Full name is required";
  else if (name.length < 2) errors.name = "Name must be at least 2 characters";
  else if (!NAME_RE.test(name)) errors.name = "Name must contain only letters and spaces";

  if (!email) errors.email = "Email is required";
  else if (!EMAIL_RE.test(email)) errors.email = "Enter a valid email address";

  if (!contact) errors.contactNumber = "Contact number is required";
  else if (!/^\d{10}$/.test(contact)) errors.contactNumber = "Contact number must be exactly 10 digits";

  if (form.dateOfBirth) {
    if (!isPastDate(form.dateOfBirth)) errors.dateOfBirth = "Date of birth must be in the past";
    else if (!isAtLeastAge(form.dateOfBirth, 18)) errors.dateOfBirth = "Employee must be at least 18 years old";
  }

  if (pan && !PAN_RE.test(pan)) errors.panNumber = "PAN must be in format ABCDE1234F";
  if (aadhar && !/^\d{12}$/.test(aadhar)) errors.aadharNumber = "Aadhar number must be exactly 12 digits";

  return errors;
}

export function validateJobRole(form) {
  const errors = {};
  const manager = String(form.manager || "").trim();
  const department = String(form.department || "").trim();
  const designation = String(form.designation || "").trim();

  if (!form.role) errors.role = "Role is required";
  if (!manager) errors.manager = "Reporting manager employee ID is required";
  else if (!MANAGER_ID_RE.test(manager)) errors.manager = "Enter a valid employee ID (letters, numbers, - or _)";

  if (!department) errors.department = "Department is required";
  else if (department.length < 2) errors.department = "Department must be at least 2 characters";

  if (designation && designation.length < 2) errors.designation = "Designation must be at least 2 characters";

  if (!form.jobLocation) errors.jobLocation = "Job location is required";
  if (!form.workMode) errors.workMode = "Work mode is required";

  if (!form.dateOfJoining) errors.dateOfJoining = "Date of joining is required";
  else {
    const join = new Date(form.dateOfJoining);
    if (Number.isNaN(join.getTime())) errors.dateOfJoining = "Enter a valid date of joining";
    else {
      const maxFuture = new Date();
      maxFuture.setFullYear(maxFuture.getFullYear() + 1);
      if (join > maxFuture) errors.dateOfJoining = "Date of joining cannot be more than 1 year in the future";
    }
  }

  return errors;
}

export function validateCompensation(form) {
  const errors = {};
  const salary = Number(form.offeredSalary);
  const tds = Number(form.TDSAmount);

  if (form.offeredSalary === "" || form.offeredSalary === null || form.offeredSalary === undefined) {
    errors.offeredSalary = "Annual CTC is required";
  } else if (Number.isNaN(salary) || salary <= 0) {
    errors.offeredSalary = "Annual CTC must be a positive number";
  }

  if (form.TDSAmount === "" || form.TDSAmount === null || form.TDSAmount === undefined) {
    errors.TDSAmount = "TDS amount is required";
  } else if (Number.isNaN(tds) || tds < 0) {
    errors.TDSAmount = "TDS amount cannot be negative";
  } else if (!Number.isNaN(salary) && salary > 0 && tds > salary / 12) {
    errors.TDSAmount = "Monthly TDS seems too high for the entered CTC";
  }

  ["annualLeave", "sickLeave", "casualleave"].forEach((key) => {
    const val = form[key];
    if (val === "" || val === null || val === undefined) {
      errors[key] = "Leave days are required";
      return;
    }
    const n = Number(val);
    if (Number.isNaN(n) || n < 0 || !Number.isInteger(n)) {
      errors[key] = "Enter a whole number 0 or greater";
    }
  });

  return errors;
}

export function validateDocuments(files) {
  const errors = {};
  const panErr = validateFile(files.panCard, { required: true });
  const aadharErr = validateFile(files.aadharCard, { required: true });
  const picErr = validateFile(files.profilePicture, { imageOnly: true });

  if (panErr) errors.panCard = panErr;
  if (aadharErr) errors.aadharCard = aadharErr;
  if (picErr) errors.profilePicture = picErr;

  ["latestDegree", "drivingLicense", "previousCompanyLetter"].forEach((key) => {
    const err = validateFile(files[key]);
    if (err) errors[key] = err;
  });

  return errors;
}

export function validateStep(step, form, files) {
  if (step === 0) return validatePersonalInfo(form);
  if (step === 1) return validateJobRole(form);
  if (step === 2) return validateCompensation(form);
  if (step === 3) return validateDocuments(files);
  return {};
}
